import { CacheProvider, EmotionCache } from '@emotion/react'
import CssBaseline from '@mui/material/CssBaseline'
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles'
import * as Sentry from '@sentry/node'
import type { NextComponentType, NextPage, NextPageContext } from 'next'
import nextCookies from 'next-cookies'
import App, { AppContext, AppInitialProps } from 'next/app'
import Head from 'next/head'
import Router from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'
import '../../public/static/css/app.css'
import '../../public/static/css/fonts.css'
import '../../public/static/css/palette.css'
import { ProcessVisitorResponse, ProcessVisitorUser, User } from '../../types'
import {
    type AppComponentProps,
    type LayoutProps,
    type SSRProps,
} from '../../types/mfc'
import {
    getColorSchemeCookie,
    setColorScheme,
    setDarkMode,
} from '../actions/app'
import { onRouteChangeFullWindow } from '../actions/layout'
import { resumePMUnreadCount } from '../actions/message'
import { pinRoom } from '../actions/room'
import { saveSettings } from '../actions/settings'
import { workerOnMessage, workerSendMessage } from '../actions/workerActions'
import { getModalState } from '../app-state/modals'
import {
    closeDialog,
    openAgeVerificationDialog,
} from '../app-state/modals/state.dialog'
import {
    openSignupDrawer,
    openTokensDrawer,
} from '../app-state/modals/state.drawer'
import {
    convertFloatingRoomToSticky,
    openFloatingRoom,
    openQuickReply,
    setFloatingPreview,
} from '../app-state/modals/state.floater'
import { pubSub } from '../app-state/pubSub'
import { getState } from '../app-state/state'
import { getRoomsState } from '../app-state/state.rooms'
import {
    getScreenState,
    resolveFullWindow,
    setScreenOrientation,
} from '../app-state/state.screen'
import { useAppStateSelector } from '../components/Hooks/useAppState'
import { useHealthAppStateSelector } from '../components/Hooks/useHealthAppState'
import { usePassive } from '../components/Hooks/usePassive'
import { useScreenStateSelector } from '../components/Hooks/useScreenState'
import { useSecureState } from '../components/Hooks/useSecureState'
import Layout from '../components/presentation/Layout'
import createEmotionCache from '../components/styles/emotion'
import theme from '../components/theme'
import {
    AppStateType,
    DisplayUeopt,
    EventType,
    ListType,
    ModalEvent,
    SecureStateType,
    SettingsType,
    UserEvent,
} from '../constants/enums'
import { sentryIgnoreList } from '../constants/errorReporting'
import { storeTipDefaults } from '../mfcLogic/api/tip'
import {
    authenticated,
    getAgeVerificationCookie,
    getUsername,
    setAgeVerificationCookie,
} from '../mfcLogic/auth'
import { getVisitorInformation } from '../mfcLogic/getVisitorInformation'
import { UserAccess } from '../mfcLogic/userProperties/level'
import { MUTATOR_MAP } from '../state/mutators'
import { pubSubList, pubSubUsers } from '../state/pubSub'
import { callbacks } from '../state/pubSub/createPubSub'
import { setupSubscriptions } from '../state/pubSub/subscriptions'
import {
    IApp,
    pauseSocketConnection,
    resumeSocketConnection,
    updateAppState,
} from '../state/state.app'
import '../state/state.keys'
import { List, activeLists, initListChanges } from '../state/state.lists'
import { setPMChatOpenState } from '../state/state.pm'
import { AppRouter, RouteHistoryItem } from '../state/state.router'
import { SETTINGSSTATE } from '../state/state.settings'
import {
    initializeSidebarState,
    onRouteChangeSidebarType,
} from '../state/state.sidebar'
import {
    USERSSTATE,
    getSlugAndId,
    getUser,
    idToSlug,
    slugToID,
} from '../state/state.users'
import { pageview, shouldReportAnalytics } from '../utilities/analytics'
import {
    isAndroid,
    isBrowser,
    isMobile as isClientMobile,
    isIDevice,
    isIpad,
} from '../utilities/browser'
import { isServer, ssrIsIOS, ssrIsMobile } from '../utilities/browserSSR'
import { getHeight, getWidth } from '../utilities/dimensions'
import { scrollIntoViewIfNeeded } from '../utilities/polyfills'
import '../utilities/router'
import { routerPush } from '../utilities/router'

// emotion CSS client side session cache
const clientSideCSSCache = createEmotionCache()

const BLUR_RADIUS = 60
if (typeof window !== 'undefined') {
    window.__PRESERVE_PAGE_STATE__ = {}
    // some functions for conveniently checkings things through the console
    window.__MFC_APP_HELPERS__ = {
        getSlugAndId,
        pinRoom,
        openQuickReply,
        openFloatingRoom,
        convertFloatingRoomToSticky,
        setFloatingPreview,
        setNsfwValue: (blurRadius = BLUR_RADIUS) =>
            updateAppState(AppStateType.APP, { nsfw: blurRadius }),
        getUser,
        getDimensions: () => [getHeight(), getWidth()],
        getRoomsState,
        getScreenState,
        getState,
        pubSub,
        callbacks,
    }
}

if (
    process.env.SENTRY_ENVIRONMENT &&
    process.env.SENTRY_DSN !== undefined &&
    process.env.APP_VERSION !== 'local-dev-build'
) {
    Sentry.init({
        attachStacktrace: true,
        dsn: process.env.SENTRY_DSN,
        enabled: true,
        environment: process.env.SENTRY_ENVIRONMENT,
        ignoreErrors: sentryIgnoreList,
        maxBreadcrumbs: 100,
        release: process.env.APP_VERSION,
        sampleRate: process.env.SENTRY_ENVIRONMENT === 'staging' ? 1 : 0.2,
        beforeBreadcrumb(breadcrumb, hint) {
            try {
                if (breadcrumb.category.startsWith('ui')) {
                    const message =
                        hint?.event?.target?.getAttribute('data-testid') ||
                        hint?.event?.target?.innerHTML
                    breadcrumb.message = message
                }
            } catch (_e) {
                return breadcrumb
            }
            return breadcrumb
        },
    })
}

// See https://www.npmjs.com/package/@sentry/node
if (process.version) Sentry.getGlobalScope().setTag('nodejs', process.version)
Sentry.getGlobalScope().setTag(
    'runtimeEngine',
    isBrowser() ? 'browser' : 'server',
)

// track analytics on route change for PageViews in Production

if (typeof window !== 'undefined' && window._NEXTJS_FASH_REFRESH_HACK) {
    // SingletonRouter.events.on('routeChangeComplete', () => {
    AppRouter.events.onCompleted(() => {
        if (window._ROUTE_CHANGE_START_) {
            window._ROUTE_CHANGE_START_ = false
        }
    })
    // SingletonRouter.events.on('routeChangeStart', () => {
    AppRouter.events.onChange(() => {
        window._ROUTE_CHANGE_START_ = true
    })
}

const shouldSkipAgeVerification = () => {
    const { skip_oapopup } = AppRouter.route.visibleQuery //SingletonRouter?.router?.query?.skip_oapopup

    if (!skip_oapopup) {
        return false
    }

    return !!Number(skip_oapopup)
}

function ageVerification() {
    if (typeof window === 'undefined') return
    const hasAgeVerificationCookie = getAgeVerificationCookie()
    const skipAgeVerification = shouldSkipAgeVerification()

    if (getUsername() || hasAgeVerificationCookie || skipAgeVerification) {
        if (!hasAgeVerificationCookie) {
            setAgeVerificationCookie()
            closeDialog()
        }
    } else {
        openAgeVerificationDialog()
    }
}

const HASH_MAP = {
    tokens: openTokensDrawer,
    signup: openSignupDrawer,
}

/** Grab all the stuff that we need on every page to properly render things on the server */
const getSsrData = (ctx: NextPageContext) => {
    const {
        user_id,
        user_lv,
        username,
        passcode,
        COLOR_SCHEME,
        SETTINGS_HOME__DISPLAY,
    } = nextCookies(ctx)

    const ssrProps: SSRProps = {
        ssr: isServer(ctx),
        uid: user_id ? Number(user_id) : undefined,
        level: user_lv ? Number(user_lv) : undefined,
        displayType: SETTINGS_HOME__DISPLAY,
        username,
        passcode,
        authenticated: !!username && !!passcode,
        darkMode:
            COLOR_SCHEME === 'dark' ||
            (COLOR_SCHEME !== 'light' &&
                ctx?.req?.headers['sec-ch-prefers-color-scheme'] === 'dark'),
        isMobile: ssrIsMobile(ctx),
        isIOS: ssrIsIOS(ctx),
    }
    return ssrProps
}

//
//
// Marc: Strategies for removing the loading screen as quickly as possible
// depending on the page we're requesting.
//
//

// Marc: Here we can determine all the pages that don't need a loading page at
// all.
// const ssrStrategy = (router: Router | NextRouter) => () => {
//     const isMessagePage = router.pathname === '/message'
//     const isNewsPage = router.pathname === '/news'
//     const isProfilePage = router.pathname === '/profile'
//     const isRoomPage = router.pathname === '/room'

//     return isMessagePage || isNewsPage || isProfilePage || isRoomPage
//         ? false
//         : true
// }

// Marc: Here we can do it based on minimal list count ,useful for the home
// page.
const listItemsStrategy = (
    setLoadingState: () => void,
    listType = ListType.DEFAULT,
    count = 10,
) => {
    if (activeLists[listType]?.size() >= count) {
        setLoadingState()

        return () => {}
    }

    const callback = (list: List) => {
        if (list.size() < count) {
            return
        }

        setLoadingState()
        unsubscribe()
    }

    const unsubscribe = pubSubList.on(listType, callback)

    return unsubscribe
}

// Marc: Helper to deal with a few loading strategies...
const useLoadingStrategy = (
    setLoadingState: (orientationOverride?: boolean) => void,
    loading: boolean,
    connected: boolean,
    // router: Router | NextRouter,
) => {
    useEffect(() => {
        if (!connected || !loading) {
            return
        }

        const route = AppRouter.route.visiblePage //router.asPath.toLowerCase()
        // const [, route] = asPath.split('/')
        const isMessagePage = route === 'message'
        const isNewsPage = route === 'news'
        const isProfilePage = route === 'profile'
        const isRoomPage = route === 'room'
        if (isRoomPage || isMessagePage || isProfilePage) {
            const floatingRoom = getModalState().floating_room
            // DG: this doesn't feel like a great spot, but we need an easy way
            // to tell the loading screen to stay a bit longer for rooms
            if (floatingRoom.show) {
                setLoadingState()
                return
            } else {
                return pubSub.MODALS.on(ModalEvent.UPDATE_FLOATING_ROOM, () => {
                    if (getModalState().floating_room.show) {
                        setLoadingState()
                    }
                })
            }
        }
        if (isNewsPage && loading) {
            return pubSub.SCREEN.on('state', (state) => {
                if (state.portrait !== null) {
                    setLoadingState(false)
                }
            })
        }

        const isHomePage = AppRouter.route.visiblePage === ''

        if (!isHomePage) {
            setLoadingState()
            return
        }

        const unsubscribeListStrategy = listItemsStrategy(setLoadingState)

        return () => {
            unsubscribeListStrategy()
        }
    }, [setLoadingState, connected, loading])

    //
    // Marc: Depending on the user's settings and if they have no friends or
    // bookmarks, we could end up with an empty home page. Here, we will use the
    // "MODELS_LOADED" event to safe guard us against loading strategies that
    // depend on waiting for users to show up (e.g. the home page strategy).
    //
    useEffect(() => {
        const route = AppRouter.route.visiblePage
        const isRoomPage = route === 'room'
        const isMessagePage = route === 'message'
        if (!isRoomPage && !isMessagePage) {
            const callback = () => {
                setLoadingState(false)
            }

            return pubSub.USERS.on(UserEvent.MODELS_LOADED, callback)
        }
    }, [setLoadingState])
}

/**
 * Scrolls the input into view on Android devices.
 * This is necessary because the keyboard will cover the input
 * and the user will not be able to see what they are typing.
 */
const useScrollInputIntoView = (socketConnected: boolean) => {
    const scrollTimer = useRef<number>(null)

    useEffect(() => {
        if (!isAndroid) return

        if (!socketConnected) {
            return () => cancelAnimationFrame(scrollTimer.current)
        }

        const input =
            document.activeElement.tagName === 'TEXTAREA' ||
            document.activeElement.tagName === 'INPUT'
        if (!input) {
            return () => cancelAnimationFrame(scrollTimer.current)
        }

        if (!document.activeElement.scrollIntoViewIfNeeded) {
            Element.prototype.scrollIntoViewIfNeeded = scrollIntoViewIfNeeded
        }
        cancelAnimationFrame(scrollTimer.current)
        scrollTimer.current = requestAnimationFrame(() => {
            document.activeElement.scrollIntoViewIfNeeded()
        })

        return () => {
            cancelAnimationFrame(scrollTimer.current)
        }
    }) // runs on every render
}

type MyAppInitialProps<P = Record<string, never>> = { pageProps: P }
type MyAppPropsType<P = Record<string, never>> = MyAppInitialProps<P> & {
    Component: NextComponentType<NextPageContext, any, AppComponentProps>
    // router: NextRouter
    __N_SSG?: boolean
    __N_SSP?: boolean
    __N_RSC?: boolean
}

type MyAppProps<P = Record<string, never>> = MyAppPropsType<P> & {
    visitorInformation: ProcessVisitorResponse
    emotionCache: EmotionCache
    page: NextPage<P>
}

const MyApp = (props: MyAppProps<SSRProps>) => {
    const {
        emotionCache = clientSideCSSCache,
        visitorInformation,
        // Component,
        pageProps,
        // page,
    } = props

    const intervalRef = useRef<ReturnType<typeof setInterval>>(null)
    const timeout = useRef<ReturnType<typeof setTimeout>>(null)
    const timming = useRef<ReturnType<typeof Date.now>>(null)
    // const router =
    //     typeof window !== 'undefined' ? SingletonRouter.router : props.router

    const [loading, setLoadingState] = useState(true) //ssrStrategy(router))
    const [verifyingAge, setVerifyAgeState] = useState(true)
    const socketConnected = useHealthAppStateSelector('connected')
    const passiveMode = usePassive()

    const resolveLoadingState = useCallback(
        (orientationOverride = true) => {
            //
            // Marc (4/28/2023): We are calling "setScreenOrientation()" here
            // because the server always renders in portrait mode and we need to
            // first render the same thing on the client before setting the correct
            // orientation. The loading screen hides the initial mismatch. This was
            // previously being handled by cookies, but that causes issues when the
            // orientation has changed while off the app, and the cookie becomes
            // outdated.
            setScreenOrientation({ overrideSkip: orientationOverride })
            resolveFullWindow()
            setTimeout(() => {
                setLoadingState(false)
            }, 0)
        },
        [setLoadingState],
    )

    const darkModeState = useAppStateSelector(AppStateType.APP, 'darkMode')
    const { ageGateActive: ageGateState } = useSecureState(
        SecureStateType.AGE_VERIFICATION,
    )

    useLoadingStrategy(
        resolveLoadingState,
        loading || ageGateState === undefined,
        socketConnected,
        // router,
    )

    const nsfw = useAppStateSelector(AppStateType.APP, 'nsfw')

    const {
        ssr = false,
        darkMode: ssrDarkMode = false,
        displayType,
        isMobile = isClientMobile(),
        isIOS,
        level: ssrLevel,
        uid: ssrUID,
        username,
        passcode,
    } = pageProps

    const [level, setUserLevel] = useState(ssrLevel)
    const [uid, setUserID] = useState(ssrUID)

    const windowWidth = useScreenStateSelector('windowWidth')
    const isAuthenticated = ssr ? pageProps.authenticated : authenticated()
    const darkMode = darkModeState ?? ssrDarkMode

    const layoutProps: LayoutProps = {
        loading: verifyingAge || loading,
        authenticated: isAuthenticated,
        isMobile,
        darkMode,
        username,
        level,
        uid,
        ssr,
    }
    // const componentProps: AppComponentProps = { ssr }

    useEffect(() => {
        getVisitorInformation(visitorInformation).then((props) => {
            if (!props) {
                const appState = { maintenanceMode: true }
                updateAppState(AppStateType.APP, appState)
                return
            }

            setVerifyAgeState(false)

            const { ageGateActive, user } = props

            if (ageGateActive) {
                // close the accept terms modal
                closeDialog()
            } else {
                ageVerification()
            }

            if (!user) {
                return
            }

            const { level, id } = user

            slugToID['me'] = id
            idToSlug[id] = 'me'
            const userState: User = {
                id,
                level,
                name: user.username,
                slug: user.username?.toLowerCase(),
                ...(user.token ? { token: user.token } : {}),
                ...(user.rewards_points
                    ? { rewardsPoints: user.rewards_points }
                    : {}),
            }
            USERSSTATE[id] = { ...userState, ...(USERSSTATE[id] || {}) }

            setUserLevel(level)
            setUserID(id)
            if (id) pubSubUsers.emit(UserEvent.ME_ID)
            else pubSubUsers.emit(UserEvent.ME_GUEST)
        })
    }, [])

    useEffect(() => {
        //
        // Worker communication channel
        //
        const unsubWorkerOnMessage = workerOnMessage((event) => {
            clearTimeout(timeout.current)

            if (!event.data) return

            const { type } = event.data

            const mutator = MUTATOR_MAP[type]

            // handle packet events from the worker: state mutations and side effects
            mutator?.(event)
        })

        const reviveWorker = () => {
            //
            // Marc: Since iOS completely stops the app when its not in view
            // we will pause and resume the FCS socket accordingly
            //
            if (isIDevice) {
                if (document.hidden) {
                    pauseSocketConnection()
                }

                if (!document.hidden) {
                    pauseSocketConnection()
                    setTimeout(() => {
                        resumeSocketConnection()
                    }, 100)
                }
            }

            if (document.hidden) {
                timming.current = Date.now()
                return
            }

            if (timming.current === null) {
                timming.current = Date.now()
            }

            //
            // Marc: This is a backup in case the worker itself has died. It is
            // not related to the FCS socket, it related to the ping pong
            // messaging that we do between the frontend and worker. Search for
            // "EventType.PING"
            //
            timeout.current = setTimeout(() => {
                location.reload()
            }, 10000)

            //
            // Marc: Lets figure out how long it's been hidden
            //
            const millisecondsHidden = Date.now() - timming.current

            //
            // Marc: Android is much more tolerant and we can wait around five
            // minutes to reset the app. Given the timeframe, it's best to just
            // reload
            //
            if (isAndroid && millisecondsHidden > 300000) {
                location.reload()
                return
            }
        }

        document.addEventListener('visibilitychange', reviveWorker)

        const updateDarkMode = (e: MediaQueryListEvent) => {
            if (SETTINGSSTATE.DEFAULT.colorScheme === 'system') {
                setDarkMode(e.matches)
            }
        }
        const darkModeQuery = window.matchMedia('(prefers-color-scheme: dark)')
        darkModeQuery.addEventListener('change', updateDarkMode)

        //
        // Logic to restart app if WebWorker disconnects
        //
        intervalRef.current = setInterval(() => {
            // console.log('Pinging Worker!')
            workerSendMessage(EventType.PING)
        }, 1000)

        const unsubSubscriptions = setupSubscriptions()

        const listChangesInterval = initListChanges()

        // Initiate data flow
        workerSendMessage(EventType.READY)

        const darkModeCookie = getColorSchemeCookie()
        setColorScheme(darkModeCookie, true)
        initializeSidebarState(windowWidth)

        // const hash = SingletonRouter.asPath.match(/#([a-z0-9]+)/gi)?.[0]
        const modalFn = HASH_MAP[AppRouter.route.hash]
        modalFn?.()

        return () => {
            document.removeEventListener('visibilitychange', reviveWorker)
            darkModeQuery.removeEventListener('change', updateDarkMode)
            clearInterval(listChangesInterval)
            clearInterval(intervalRef.current)
            clearTimeout(timeout.current)
            unsubWorkerOnMessage()
            unsubSubscriptions()
        }
    }, [])

    useScrollInputIntoView(socketConnected)

    // support guest users saving settings
    // however, let logged in users be updated via ueopt when FCS connects
    useEffect(() => {
        if (level && level >= 1) return
        if (
            level === 0 &&
            (displayType === DisplayUeopt.default ||
                displayType === DisplayUeopt.detailed ||
                displayType === DisplayUeopt.preview ||
                displayType === DisplayUeopt.text)
        )
            saveSettings(
                SettingsType.DEFAULT,
                'display',
                displayType as DisplayUeopt,
            )
    }, [displayType, level])

    useEffect(() => {
        if (level === UserAccess.MEMBER_PREMIUM) {
            storeTipDefaults()
        }
    }, [level])

    useEffect(() => {
        const newAppState = {
            isMobile,
            isIOS,
            isIpad: !!isIpad,
            nsfw:
                nsfw || (process.env.NSFW === 'true' ? BLUR_RADIUS : undefined),
            passcode: passcode || 'guest',
            authenticated: isAuthenticated,
        } as IApp
        // if (username) newAppState.name = username
        // if (typeof level === 'number') newAppState.level = level
        // if (typeof uid === 'number') newAppState.uid = uid
        updateAppState(AppStateType.APP, newAppState)
    }, [isMobile, isIOS, isAuthenticated, passcode, username, uid, level])

    return (
        <CacheProvider value={emotionCache}>
            <Head>
                <meta charSet="utf-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta
                    name="viewport"
                    content="width=device-width,initial-scale=1,minimum-scale=1,maximum-scale=1,user-scalable=no,viewport-fit=cover"
                />
                {darkMode ? (
                    <meta name="theme-color" content="rgb(30, 30, 30)" />
                ) : (
                    <meta name="theme-color" content="#FFFFFF" />
                )}
                {!passiveMode && <title>MyFreeCams App</title>}
                <meta
                    name="description"
                    content="MyFreeCams is the original free webcam community for adults, featuring live video chat with thousands of models, cam girls, amateurs and female content creators!"
                />
                <meta
                    name="Keywords"
                    content="MyFreeCams,free,cams,webcams,live,video,chat,sex,girls,nude,amateurs,models,adults,community,camgirls,pornstars,camming,sound"
                />
                <meta version={process.env.APP_VERSION} />
                <meta data-env={process.env.NODE_ENV} />
                <link rel="manifest" href="/static/manifest.json" />
                <link
                    rel="icon"
                    sizes="any"
                    href="https://newassets.mfcimg.com/static/icons/favicon.ico"
                />
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href="/static/icons/favicon-32x32.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="180x180"
                    href="/static/icons/apple-touch-icon-180x180.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="152x152"
                    href="/static/icons/apple-touch-icon-152x152.png"
                />
                <link
                    rel="apple-touch-icon"
                    sizes="120x120"
                    href="/static/icons/apple-touch-icon-120x120.png"
                />
                <link
                    rel="mask-icon"
                    href="/static/icons/safari-pinned-tab.svg"
                />
                <meta name="msapplication-TileColor" content="#FFFFFF" />
                <meta
                    name="msapplication-config"
                    content="/static/icons/browserconfig.xml"
                />
                {nsfw && (
                    <style
                        dangerouslySetInnerHTML={{
                            __html: `img, video {
                        filter: blur(${nsfw}px) !important;
                    }`,
                        }}
                    />
                )}
            </Head>
            <StyledEngineProvider>
                <ThemeProvider theme={theme(darkMode)}>
                    <CssBaseline />
                    <Layout {...layoutProps}>
                        {/* <Component {...componentProps} /> */}
                    </Layout>
                </ThemeProvider>
            </StyledEngineProvider>
        </CacheProvider>
    )
}

/**
 * Optional method that allows for overwriting and full control of the `page`
 * initialization. This allows for keeping state between navigation,
 * custom error handling, injecting additional data.
 *
 * Using `getInitialProps` disables the ability to perform automatic static
 * optimization, causing every page in your app to be server-side rendered.
 */
MyApp.getInitialProps = async (appContext: AppContext) => {
    // calls page's `getInitialProps` and fills `appProps.pageProps`
    const props = await App.getInitialProps(appContext)

    const { pageProps, ...appProps } = props

    const ctx = appContext.ctx as NextPageContext & {
        req: {
            ip: string
            visitorInformation?: ProcessVisitorResponse
        }
        me: User
    }

    const visitorInformation = ctx.req?.visitorInformation
    const { user = {} as ProcessVisitorUser } = visitorInformation || {}

    const {
        rewards_points: rewardsPoints,
        access_level: level = 0,
        username = '',
        id = 0,
        tokens,
        token,
        // status: 'premium',
        // emails
    } = user

    const name = username || ''
    const slug = name.toLowerCase() || 'me'
    const me = {
        rewardsPoints,
        tokens,
        token,
        level,
        slug,
        name,
        id,
    }

    ctx.me = visitorInformation ? me : ({} as User)

    // grab Authentication status, SSR, isMobile, and Dark Mode since thats needed everywhere
    const ssrProps = getSsrData(appContext.ctx)
    // console.log(
    //     '[_app.getInitialProps] return props',
    //     appProps,
    //     pageProps,
    //     ssrProps,
    // )

    const initialProps: AppInitialProps & {
        visitorInformation: ProcessVisitorResponse
    } = {
        ...appProps,
        visitorInformation,
        pageProps: {
            ...(Object.keys(pageProps).length > 0 ? pageProps : {}),
            ...ssrProps,
        },
    }

    AppRouter.init(appContext.router.asPath)

    return initialProps
}

AppRouter.events.onReady((initialPath) => {
    if (typeof window !== 'undefined') {
        // console.log('[onReady]', Router.router.onPopState)

        // safe check for vitest
        if (Router && Router.router) {
            // remove nextjs's onPopState listener so we don't have weird collisions
            window.removeEventListener('popstate', Router.router.onPopState)
        }
        window.addEventListener('popstate', (event) => {
            // console.log('[popstate listener]', event)

            // if event.state is null or missing our routes (because initial load will have an entry from nextjs),
            // we should just load the initial path
            const { visibleRoute = initialPath, baseRoute = initialPath } =
                (event.state || {}) as RouteHistoryItem
            routerPush(visibleRoute, {
                baseRoute: baseRoute,
                replaceState: true,
            })
        })
    }
    const initialOptions = {
        initial: true,
        // ensure we replace the nextjs history state so a user doesn't
        // hit "back" and find themselves with weird url state
        replaceState: true,
    }

    routerPush(initialPath, initialOptions)
})

AppRouter.events.onChange((routeInfo) => {
    if (typeof window !== 'undefined') {
        if (
            routeInfo.currentRoute === 'message' &&
            routeInfo.currentSlug !== routeInfo.pendingSlug
        ) {
            const currentRouteID = slugToID[routeInfo.currentSlug.toLowerCase()]
            resumePMUnreadCount(currentRouteID)
            setPMChatOpenState(currentRouteID, false)
        }
    }
})
AppRouter.events.onCompleted((routeInfo) => {
    if (typeof window !== 'undefined') {
        // console.log('[onCompleted] callback', routeInfo)
        onRouteChangeSidebarType(routeInfo.fullRoute)
        onRouteChangeFullWindow(routeInfo.fullRoute)
        if (shouldReportAnalytics()) pageview(routeInfo.fullRoute)
    }
})

export default MyApp

export const thisIsAnUnusedExport =
    'this export only exists to disable fast refresh for this file'
